import { useEffect, useState, useCallback } from 'react';

import { hasValue } from '@/modules/numberUtils';
import useShortcut from '@/hooks/useShortCutHook';

export default function useSongPlayer({
  songRef,
  playing,
  onReady,
  onStart,
  onProgress,
  onRestart,
  forceRestart,
  seekTo,
  onPlayPause,
  spaceShortCut,
}) {
  const [state, setState] = useState({
    mounted: false,
    ready: false,
    playing: false,
    volume: 1,
    duration: 0,
    bufferTime: 0,
    currentTime: 0,
    isBrowser: false,
    playbackRate: 1,
  });

  const actions = {
    setState: (newState) => {
      setState((previousState) => ({ ...previousState, ...newState }));
    },
    onDuration: (duration) => {
      actions.setState({
        duration: state.duration || duration,
        ready: true,
      });
      console.log('Player has media duration: ', duration, ' seconds.');
      if (onReady) onReady(duration);
    },
    onReadyToPlay: () => {
      console.log('Player is ready to play');
    },
    onStart: onStart ? onStart : () => {},
    onProgress: ({ playedSeconds, loadedSeconds }) => {
      actions.setState({
        currentTime: playedSeconds,
        bufferTime: loadedSeconds,
        ended: playedSeconds >= state.duration,
      });
      if (onProgress) {
        onProgress({ playedSeconds, duration: state.duration });
      }
    },
    play: () => {
      if (onPlayPause) onPlayPause(true);
      actions.setState({ playing: true });
      //pressing play at the end of the song restarts the song. Call the startCallback then
      if (state.currentTime >= state.duration) {
        actions.restart();
      }
    },
    pause: () => {
      if (onPlayPause) onPlayPause(false);
      actions.setState({ playing: false });
    },

    seek: (position) => {
      songRef.current?.seekTo(position, 'seconds');
    },
    restart: () => {
      actions.setState({ currentTime: 0 });
      actions.seek(0);
      if (onRestart) onRestart();
    },
    changePlaybackRate: (playbackRate) => {
      actions.setState({ playbackRate });
    },
  };

  useShortcut({
    active: spaceShortCut,
    shortcut: ' ',
    handler: onSpacePress,
    focusTagExclude: ['INPUT'],
  });

  useEffect(() => {
    actions.setState({
      currentTime: 0,
    });
  }, [state.duration]);

  useEffect(() => {
    actions.setState({ playing });
  }, [playing]);

  useEffect(() => {
    if (forceRestart) {
      actions.seek(0);
      actions.setState({ playing: true, currentTime: 0 });
      if (onRestart) onRestart();
    }
  }, [forceRestart]);

  useEffect(() => {
    // React player does seek to 0 after the player is ready, so we need to wait for it. state.duration is set when the player is ready
    if (state.duration && hasValue(seekTo) && state.playing) {
      actions.seek(seekTo);
    }
  }, [seekTo, state.duration, state.playing]);

  function onSpacePress() {
    if (state.playing) {
      actions.pause();
    } else {
      actions.play();
    }
  }

  useEffect(() => {
    actions.setState({ isBrowser: true });
  }, []);

  return { state, actions };
}
